import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../UserContext';
import { MenuItems } from './MenuItems';
import logo from '../../assets/images/logo.png';
import logoText from '../../assets/images/logo-text.png';
import coinImg from '../../assets/images/coin-img.png';
import useMatchMedia from '../utils/useMatchMedia';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { Amplify, Auth } from 'aws-amplify';
import { Hub } from '@aws-amplify/core';
import awsconfig from '../../aws-exports';
import { css, cx, keyframes } from '@emotion/css';
import Lottie from 'react-lottie';
import { default as animationData } from '../../assets/animation_llwbajur.json';
import { LocationContext } from '../../reducers/LocationContext';
import { SubscriptionContext } from '../../reducers/SubscriptionContext';

Amplify.configure(awsconfig);

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData,
  rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
};

// ─── Design tokens ────────────────────────────────────────────────────────────
const ORANGE = '#f16602';
const NAV_BG = 'linear-gradient(135deg, #f16602 0%, #e05500 40%, #c94400 100%)';
const NAV_BORDER = 'rgba(255,255,255,0.12)';

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const drawerIn = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(0); }
`;

// ─── Styles ───────────────────────────────────────────────────────────────────
const marqueeScroll = keyframes`
  0% { transform: translateX(100vw); }
  100% { transform: translateX(-100%); }
`;

const marqueeBanner = css`
  position: fixed;
  top: 64px;
  left: 0;
  width: 100%;
  height: 36px;
  background-color: #f4e519ff;
  display: flex;
  align-items: center;
  z-index: 990;
  border-bottom: 2px solid ${ORANGE};
  font-family: 'Inter', sans-serif;
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
`;

const marqueeWrapper = css`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  align-items: center;
  height: 100%;
`;

const marqueeText = css`
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  color: #0d0d0dff;
  animation: ${marqueeScroll} 25s linear infinite;
  
  &:hover {
    animation-play-state: paused;
  }
`;

const marqueeHighlight = css`
  color: ${ORANGE};
  font-weight: 800;
  padding: 0 4px;
`;

const marqueeBtn = css`
  background: linear-gradient(135deg, ${ORANGE} 0%, #d94f00 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  margin: 0 16px;
  text-transform: uppercase;
  flex-shrink: 0;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(241,102,2,0.35);
    color: #fff;
  }
`;

const headerStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px;
  background: ${NAV_BG};
  border-bottom: 1px solid ${NAV_BORDER};
  font-family: 'Inter', sans-serif;
  box-shadow: 0 4px 20px rgba(200, 68, 0, 0.35);
  transition: box-shadow 0.3s;

  @media (max-width: 900px) { padding: 0 16px; }
`;

const scrolledStyle = css`
  box-shadow: 0 6px 28px rgba(180, 55, 0, 0.5);
`;

const logoWrap = css`
  display: flex;
  align-items: center;
  text-decoration: none;
  flex-shrink: 0;
  img {
    height: 36px;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }
`;

const navLinks = css`
  display: flex;
  align-items: center;
  gap: 4px;
  list-style: none;
  margin: 0;
  padding: 0;
`;

const navItem = css`
  position: relative;
`;

const linkBase = css`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.88);
  text-decoration: none;
  cursor: pointer;
  border: none;
  background: transparent;
  transition: color 0.2s, background 0.2s;
  white-space: nowrap;

  &:hover {
    color: #fff;
    background: rgba(255,255,255,0.15);
  }
`;

const activeLinkStyle = css`
  color: #fff !important;
  background: rgba(255,255,255,0.2) !important;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.25);
`;

const caretStyle = css`
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid currentColor;
  transition: transform 0.25s;
  flex-shrink: 0;
`;

const caretOpenStyle = css`
  transform: rotate(180deg);
`;

const dropdownMenu = css`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  min-width: 220px;
  background: #ffffff;
  border: 1px solid rgba(241,102,2,0.15);
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  z-index: 9999;
  overflow: hidden;
`;

const dropdownLink = css`
  display: block;
  padding: 11px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #2a1200;
  text-decoration: none;
  transition: background 0.18s, color 0.18s, padding-left 0.18s;

  &:hover {
    background: rgba(241,102,2,0.1);
    color: ${ORANGE};
    padding-left: 22px;
  }
`;

const rightPanel = css`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const coinBadge = css`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(241,102,2,0.12);
  border: 1px solid rgba(241,102,2,0.28);
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #ffaa55;

  img { width: 18px; height: 18px; object-fit: contain; }
`;

const avatarWrap = css`
  position: relative;
  cursor: pointer;

  img.avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid ${ORANGE};
    object-fit: cover;
  }
`;

const profileMenu = css`
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  min-width: 200px;
  background: linear-gradient(160deg, #fff9f4 0%, #fdfbf7 100%);
  border: 1.5px solid rgba(241,102,2,0.2);
  border-radius: 14px;
  padding: 8px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.5) inset;
  animation: ${slideDown} 0.22s ease;
  z-index: 200;
`;

const profileLinkStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #2a1200;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.18s, color 0.18s;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;

  &:hover {
    background: rgba(241,102,2,0.1);
    color: ${ORANGE};
  }

  img { width: 16px; height: 16px; object-fit: contain; filter: brightness(0) invert(0.3); }
`;

const profileHeading = css`
  padding: 10px 14px 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(80,30,0,0.55);
`;

const dividerStyle = css`
  height: 1px;
  background: rgba(241,102,2,0.18);
  margin: 6px 8px;
`;

const loginBtn = css`
  padding: 8px 20px;
  background: linear-gradient(135deg, ${ORANGE} 0%, #d94f00 100%);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  transition: transform 0.18s, box-shadow 0.18s;
  box-shadow: 0 4px 16px rgba(241,102,2,0.35);

  &:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(241,102,2,0.45); }
`;

// Mobile
const hamburgerBtn = css`
  width: 38px;
  height: 38px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  border: none;
  background: rgba(255,255,255,0.07);
  border-radius: 10px;
  cursor: pointer;
  padding: 0;

  span {
    display: block;
    width: 20px;
    height: 2px;
    background: rgba(255,255,255,0.8);
    border-radius: 2px;
    transition: all 0.25s;
  }
`;

const drawerOverlay = css`
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(4px);
`;

const drawerPanel = css`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  background: rgba(15, 7, 2, 0.98);
  border-right: 1px solid ${NAV_BORDER};
  padding: 24px 0 40px;
  animation: ${drawerIn} 0.28s cubic-bezier(0.22,1,0.36,1);
  overflow-y: auto;
`;

const drawerClose = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 20px;
  border-bottom: 1px solid rgba(241,102,2,0.12);
  margin-bottom: 12px;
`;

const drawerLink = css`
  display: block;
  padding: 13px 24px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.75);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.18s, background 0.18s;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;

  &:hover { color: ${ORANGE}; background: rgba(241,102,2,0.07); }
`;

const drawerSubLink = css`
  display: block;
  padding: 11px 40px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.5);
  text-decoration: none;
  transition: color 0.18s;

  &:hover { color: ${ORANGE}; }
`;

// ─── Component ────────────────────────────────────────────────────────────────
function Navbar() {
  const { user, setUser } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [loadingstatus, setLoadingstatus] = useState(false);
  const { isMobile } = useMatchMedia();
  const [isOpen, setIsOpen] = useState(false);         // mobile drawer
  const [scrolled, setScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);          // profile
  const [isChallengesDropdownOpen, setIsChallengesDropdownOpen] = useState(false);
  const [mobileSubOpen, setMobileSubOpen] = useState(false);
  const { subscriptionStatus, remainingTime } = useContext(SubscriptionContext);
  const { countryCode } = useContext(LocationContext);
  const [displayTime, setDisplayTime] = useState(remainingTime);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => { setDisplayTime(remainingTime); }, [remainingTime]);

  useEffect(() => {
    if (!remainingTime || subscriptionStatus !== 'active') return;
    const interval = setInterval(() => {
      setDisplayTime((prev) => {
        if (!prev) return prev;
        let { days, hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) { seconds = 59; minutes--; }
        if (minutes < 0) { minutes = 59; hours--; }
        if (hours < 0) { hours = 23; days--; }
        if (days < 0) return null;
        return { days, hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [remainingTime, subscriptionStatus]);

  const getToken = async () => {
    setLoading(true);
    try {
      const u = await Auth.currentAuthenticatedUser();
      const res = await Auth.currentSession();
      const accessToken = res.getAccessToken();
      const jwt = accessToken.getJwtToken();
      if (accessToken.payload.username.includes('Google_')) {
        await CreatorUpdateUser(accessToken.payload.username, u.attributes['email'], jwt);
      } else {
        await CreatorUpdateUser(accessToken.payload.username, accessToken.payload.username, jwt);
      }
    } catch (_) { }
    finally { setLoading(false); }
  };

  const getTokenByEvent = async (data) => {
    setLoading(true);
    setLoadingstatus(true);
    switch (data.payload.event) {
      case 'signIn': getToken(); break;
      case 'signOut': setUser({}); setLoadingstatus(false); setLoading(false); break;
      default: break;
    }
    setLoading(false);
  };

  const CreatorUpdateUser = async (username, email, jwt) => {
    if (username.includes('Google')) username = email;
    const now = new Date();
    const res = await fetch('https://api.quizwizard.net/quiz/createorupdateuser', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: jwt },
      body: JSON.stringify({
        username, score: 1000, appname: 'QuizWhiz', reloadedpoints: 1000,
        canbeprivate: true, grade: 5, timezone: 'UTC-5', birthdate: '2000-01-01',
        parent: false, parentname: ' ', parentnamerequestedname: '',
        customizequestions: false, parentnamerequested: false,
        profilepic: 'defaultavatar', premiumuser: 0,
        friendslist: [], friendsrequestlist: [], sendrequestlist: [],
        friendnamerequested: false, rewardslist: [], crown: '',
        user_created_time: now.toUTCString(),
        availableavatars: [
          'boy1', 'girl1', 'boy2', 'girl2', 'boy3', 'girl3', 'boy4', 'girl4',
          'boy5', 'girl5', 'boy6', 'girl6', 'boy7', 'girl7', 'boy8', 'girl8', 'boy9', 'girl9', 'boy10', 'girl10',
        ],
        nextbooster: 10, nextunlockkey: 5, unlockkeys: 0, boosters: 0,
        batchlist: [], gold: 0, id: username,
      }),
    });
    const data = await res.json();
    if (data['isnewuser']) history.push('/settings');
    setUser(data);
  };

  useEffect(() => {
    Hub.listen('auth', getTokenByEvent);
    getToken();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => { setIsOpen(false); }, [location]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handler = () => { setIsDropdownOpen(false); setIsChallengesDropdownOpen(false); };
    document.body.addEventListener('click', handler);
    return () => document.body.removeEventListener('click', handler);
  }, []);

  const toggleChallenges = (e) => {
    e.stopPropagation();
    if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    setIsChallengesDropdownOpen(prev => !prev);
  };

  const toggleProfile = (e) => {
    e.stopPropagation();
    if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    setIsDropdownOpen(prev => !prev);
  };

  const isActive = (item) => {
    if (item.url && location.pathname.includes(item.url)) return true;
    if (item.subMenu) return item.subMenu.some(s => location.pathname.includes(s.url));
    return false;
  };

  const handlePremiumLinkClick = (e) => {
    e.preventDefault();
    history.push('/subscription');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <header className={cx(headerStyle, scrolled && scrolledStyle)}>
        {/* Logo */}
        <Link className={logoWrap} to="/">
          <img src={isMobile ? logo : logoText} alt="QuizWhiz" />
        </Link>

        {/* Desktop nav */}
        {!isMobile && (
          <ul className={navLinks}>
            {MenuItems.map((item) => (
              <li key={item.title} className={navItem}>
                {item.subMenu ? (
                  <>
                    <button
                      className={cx(linkBase, isActive(item) && activeLinkStyle)}
                      onClick={toggleChallenges}
                    >
                      {item.title}
                      <span className={cx(caretStyle, isChallengesDropdownOpen && caretOpenStyle)} />
                    </button>
                    {isChallengesDropdownOpen && (
                      <div className={dropdownMenu}>
                        {item.subMenu.map((sub) => (
                          <Link
                            key={sub.title}
                            className={dropdownLink}
                            to={sub.url}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
                              setIsChallengesDropdownOpen(false);
                            }}
                          >
                            {sub.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    className={cx(linkBase, isActive(item) && activeLinkStyle)}
                    to={item.url}
                  >
                    {item.title}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Right panel */}
        <div className={rightPanel}>
          {loading ? (
            <div style={{ width: 60 }}>
              <Lottie options={defaultOptions} height={60} width={60} style={{ clipPath: 'inset(20% 10% 30% 10%)' }} />
            </div>
          ) : Object.keys(user).length ? (
            <>
              {/* Score badge */}
              <div className={coinBadge}>
                <img src={coinImg} alt="" />
                {user['score']} pts
              </div>

              {/* Avatar + dropdown */}
              <div className={avatarWrap} onClick={toggleProfile}>
                <img
                  className="avatar"
                  src={user['profilepic'] ? (user['profilepic'].startsWith('http') || user['profilepic'].startsWith('/avatar') ? user['profilepic'] : require(`../../assets/images/${user['profilepic']}.jpg`).default) : ''}
                  alt=""
                />
                {isDropdownOpen && (
                  <div className={profileMenu} onClick={(e) => e.stopPropagation()}>
                    <div className={profileHeading}>Hi, {user.username}</div>
                    <div className={dividerStyle} />
                    <button className={profileLinkStyle} onClick={handlePremiumLinkClick}>
                      <img src="/assets/crown7.png" alt="" />
                      {subscriptionStatus === 'active' && displayTime
                        ? `Subscription · ${displayTime.days === 0 ? 'Last Day' : `${displayTime.days}d left`}`
                        : 'Get Premium'}
                    </button>
                    <Link className={profileLinkStyle} to="/premiumdashboard">
                      <img src={require('../../assets/images/icon-dashboard.png').default} alt="" />
                      Transactions
                    </Link>
                    <Link className={profileLinkStyle} to="/settings">
                      <img src={require('../../assets/images/icon-settings.png').default} alt="" />
                      Settings
                    </Link>
                    <div className={dividerStyle} />
                    <Link className={profileLinkStyle} to="/logout">
                      <img src={require('../../assets/images/icon-logout.png').default} alt="" />
                      Logout
                    </Link>
                  </div>
                )}
              </div>
            </>
          ) : (
            !loadingstatus && (
              <Link className={loginBtn} to="/login">Login</Link>
            )
          )}

          {/* Mobile hamburger */}
          {isMobile && (
            <button className={hamburgerBtn} onClick={() => setIsOpen(true)}>
              <span /><span /><span />
            </button>
          )}
        </div>
      </header>

      {/* Marquee Banner */}
      {location.pathname !== '/login' && !location.pathname.includes('quiz') && !location.pathname.includes('dashboard') && !location.pathname.includes('batch') && (
        <>
          <div className={marqueeBanner}>
            <div className={marqueeWrapper}>
              <div className={marqueeText}>
                Exclusive Offer: Now get 2 weeks FREE Chess classes with Premier Chess Academy. Use code <span className={marqueeHighlight}>"QW2W"</span>
              </div>
            </div>
            <a
              href="https://premierchessacademy.com/trial-class-enrollment/"
              target="_blank"
              rel="noopener noreferrer"
              className={marqueeBtn}
            >
              Register Now
            </a>
          </div>
          <div style={{ height: '46px', width: '100%', flexShrink: 0 }} aria-hidden="true" />
        </>
      )}

      {/* Mobile Drawer */}
      {isMobile && isOpen && (
        <div className={drawerOverlay} onClick={() => setIsOpen(false)}>
          <div className={drawerPanel} onClick={(e) => e.stopPropagation()}>
            <div className={drawerClose}>
              <Link to="/" onClick={() => setIsOpen(false)}>
                <img src={logoText} alt="QuizWhiz" style={{ height: 28 }} />
              </Link>
              <button
                style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}
                onClick={() => setIsOpen(false)}
              >×</button>
            </div>

            {MenuItems.map((item) => (
              <div key={item.title}>
                {item.subMenu ? (
                  <>
                    <button
                      className={drawerLink}
                      onClick={() => setMobileSubOpen(prev => !prev)}
                    >
                      {item.title} {mobileSubOpen ? '▲' : '▼'}
                    </button>
                    {mobileSubOpen && item.subMenu.map((sub) => (
                      <Link
                        key={sub.title}
                        className={drawerSubLink}
                        to={sub.url}
                        onClick={() => setIsOpen(false)}
                      >
                        {sub.title}
                      </Link>
                    ))}
                  </>
                ) : (
                  <Link
                    className={drawerLink}
                    to={item.url}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.title}
                  </Link>
                )}
              </div>
            ))}

            {Object.keys(user).length > 0 && (
              <>
                <div className={dividerStyle} style={{ margin: '12px 20px' }} />
                <Link className={drawerLink} to="/settings" onClick={() => setIsOpen(false)}>Settings</Link>
                <Link className={drawerLink} to="/logout" onClick={() => setIsOpen(false)}>Logout</Link>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;